import { __assign, __rest } from "tslib";
// DS: DS6.1
import React, { useEffect, useRef, useCallback } from 'react';
import c from 'classnames';
import BiDiv from '@wix/da-bi/pkg/Components/BiDiv';
import PlainButtonOrLink from '../../Buttons/PlainButtonOrLink';
import { IconSize } from '../../Icons/IconWrap';
import RadioButton from '../../RadioButton';
import Flex from '../../Flex';
import Checkbox from '../../Checkbox';
import s from './SelectionListItem.scss';
/**
 * In its most basic form this acts as a clickable div that maintains
 * a selection state. It is up to you to manage that state in your parent
 * component if you have multiple selection items etc.
 *
 * When you use the control prop, you change the semantics of the component
 * it then tries to act more like that form element. This is particularly
 * true for radios where you want the group to be mutually exclusive, so
 * you need to provide value and base parent selection on value rather than
 * the clicked state. I considered breaking this up because of that but
 * for now this does make it simple to find and use from the DS as its a
 * 1-1 mapping.
 */
var SelectionListItem = function (_a) {
    var label = _a.label, subLabel = _a.subLabel, prefix = _a.prefix, suffix = _a.suffix, SuffixIcon = _a.suffixIcon, isSelected = _a.isSelected, isDisabled = _a.isDisabled, control = _a.control, fillContainerWidth = _a.fillContainerWidth, noLeftPadding = _a.noLeftPadding, showSuffixOnlyOnHover = _a.showSuffixOnlyOnHover, biData = _a.biData, onClick = _a.onClick, onChange = _a.onChange, className = _a.className, value = _a.value, name = _a.name, focused = _a.focused, href = _a.href, target = _a.target, rest = __rest(_a, ["label", "subLabel", "prefix", "suffix", "suffixIcon", "isSelected", "isDisabled", "control", "fillContainerWidth", "noLeftPadding", "showSuffixOnlyOnHover", "biData", "onClick", "onChange", "className", "value", "name", "focused", "href", "target"]);
    var itemRef = useRef(null);
    var setItemRef = useCallback(function (node) {
        itemRef.current = node;
    }, []);
    useEffect(function () {
        if (itemRef.current && focused) {
            itemRef.current.focus({ preventScroll: true });
        }
    }, [itemRef, focused]);
    var onChangeHandler = function () {
        if (onChange) {
            onChange(!isSelected, value);
        }
        else if (onClick) {
            onClick();
        }
    };
    var ItemContent = (React.createElement(React.Fragment, null,
        prefix && React.createElement("div", { className: s['prefix'] }, prefix),
        React.createElement(Flex, { direction: "column", justifyContent: "center", className: s['central-area'] },
            React.createElement("div", { className: s['label'] }, label),
            subLabel && React.createElement("div", { className: s['sub-label'] }, subLabel)),
        (suffix || SuffixIcon) && (React.createElement("div", { className: s['suffix'] },
            suffix,
            SuffixIcon && (React.createElement(SuffixIcon, { size: IconSize.MEDIUM, className: s['suffix-icon'], disableDefaultColors: true }))))));
    var wrapperClassnames = c(s['root'], isSelected && s['selected'], isDisabled && s['disabled'], noLeftPadding && s['no-left-padding'], control === undefined && s['no-control'], control === 'radiobutton' && s['radio-margin'], fillContainerWidth && s['fill-container-width'], showSuffixOnlyOnHover && s['suffix-on-hover'], className);
    // Radio buttons allow an onClick handler to be passed directly.
    // This way we can catch click events on the radio button even when it's already selected.
    if (control === 'radiobutton') {
        return (React.createElement(RadioButton, __assign({ name: name || '', value: value || '', className: wrapperClassnames, onChange: onChange && (function () { return onChange(!isSelected, value); }), onClick: onClick, biData: biData, checked: isSelected, tabIndex: 0 }, rest), ItemContent));
    }
    if (control === 'checkbox') {
        return (React.createElement(BiDiv, { biData: biData, className: wrapperClassnames, onClick: onChangeHandler, innerRef: setItemRef, tabIndex: 0 },
            React.createElement(Checkbox, __assign({ role: "checkbox", "aria-checked": isSelected, className: s['checkbox'], onChange: onChangeHandler, biData: biData, isChecked: isSelected, id: name }, rest)),
            ItemContent));
    }
    if (control === 'link') {
        return (React.createElement(PlainButtonOrLink, __assign({ className: wrapperClassnames, href: href, target: target, onClick: onChangeHandler, biData: biData, innerRef: setItemRef, tabIndex: 0 }, rest // mildly disgusting but react is being weird
        ), ItemContent));
    }
    return (React.createElement(BiDiv, __assign({ className: wrapperClassnames, onClick: onChangeHandler, biData: biData, innerRef: setItemRef, tabIndex: 0 }, rest), ItemContent));
};
SelectionListItem.displayName = 'SelectionListItem';
export default SelectionListItem;
//# sourceMappingURL=SelectionListItem.js.map